import type {Router, RouteLocation} from 'src/types/router';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import classify from 'src/utils/classify';

import {FilterCard} from './';
import Checkbox from 'src/components/lib/checkbox';

import css from './result-card.css';
import {type TranslationFunction} from 'src/types/language-support';


type Props = {
  onSave?: (e: React.SyntheticEvent) => unknown;
  onCancel?: (e: React.SyntheticEvent) => unknown;
  onChange: (e: React.SyntheticEvent<HTMLInputElement>) => unknown;
  selectedTypes: string[];
  eventTypeCounts?:
    | {
        [key: string]: number;
      }
    | null
    | undefined;
  eventTypes?: {
    value: string;
    label: string;
  }[];
  history?: Router;
  location?: RouteLocation;
};

const getResultsConverted = (t: TranslationFunction) => [
  {
    key: 'unsubscribed',
    label: t('Unsubscribed', 'Unsubscribed'),
    color: css.colorError,
  },
  {
    key: 'spam_reported',
    label: t('Spam_reported', 'Spam reported'),
    color: css.colorError,
  },
  {
    key: 'unopened',
    label: `${t('Unopened', 'Unopened')} / ${t('No_response', 'No response')}`,
    color: css.colorError,
  },
];

const getResultsPartiallyConverted = (t: TranslationFunction) => [
  {
    key: 'opened',
    label: t('Opened', 'Opened'),
    color: css.colorWarning,
  },
  {
    key: 'started',
    label: t('Started', 'Started'),
    color: css.colorWarning,
  },
  {
    key: 'partial',
    label: t('Partial', 'Partial'),
    color: css.colorWarning,
  },
];

const getResultsNotConverted = (t: TranslationFunction) => [
  {
    key: 'completed',
    label: t('Completed', 'Completed'),
  },
  {
    key: 'clicked',
    label: t('Clicked', 'Clicked'),
  },
  {
    key: 'opened_no_link',
    label: `${t('Opened', 'Opened')} (${t(
      'emails_wo_links',
      'emails w/o links',
    )})`,
  },
  {
    key: 'replied',
    label: t('Replied', 'Replied'),
  },
];

const MessageTypeCardV2 = ({
  onSave,
  onCancel,
  onChange,
  selectedTypes,
}: Props) => {
  const {t} = useLabelTranslation();
  const ResultsConverted = getResultsConverted(t);
  const ResultsPartiallyConverted = getResultsPartiallyConverted(t);
  const ResultsNotConverted = getResultsNotConverted(t);
  return (
    <FilterCard onSave={onSave} onCancel={onCancel}>
      <div className={css.container}>
        <div className={css.header}>{t('not_converted', 'not converted')}</div>
        <ul className={css.checklist}>
          {ResultsConverted.map(({key, label, color}) => (
            <li key={key}>
              <label
                className={classify(css.label, {
                  [css.labelChecked]: selectedTypes.includes(key),
                })}
                style={{color}}
              >
                <Checkbox
                  className={css.checkbox}
                  name={key}
                  checked={selectedTypes.includes(key)}
                  onChange={onChange}
                />
                {label}
              </label>
            </li>
          ))}
        </ul>
        <div className={css.header}>
          {t('partially_converted', 'partially converted')}
        </div>
        <ul className={css.checklist}>
          {ResultsPartiallyConverted.map(({key, label, color}) => (
            <li key={key}>
              <label
                className={classify(css.label, css.success, {
                  [css.labelChecked]: selectedTypes.includes(key),
                })}
                style={{color}}
              >
                <Checkbox
                  className={css.checkbox}
                  name={key}
                  checked={selectedTypes.includes(key)}
                  onChange={onChange}
                />
                {label}
              </label>
            </li>
          ))}
        </ul>
        <div className={css.header}>{t('converted', 'converted')}</div>
        <ul className={css.checklist}>
          {ResultsNotConverted.map(({key, label}) => (
            <li key={key}>
              <label
                className={classify(css.label, css.success, {
                  [css.labelChecked]: selectedTypes.includes(key),
                })}
              >
                <Checkbox
                  className={css.checkbox}
                  name={key}
                  checked={selectedTypes.includes(key)}
                  onChange={onChange}
                />
                {label}
              </label>
            </li>
          ))}
        </ul>
        <div className={css.footer}>
          {`${t(
            'A_single_message_can_have_up_to_three_conversion_types_but_only_one_is_shown_in_the_conversion_column_For_example_a_recipient_can_unsubscribe_reports_spam_and_click_on_the_same_message',
            'A single message can have up to three conversion types, but only one is shown in the conversion column. For example, a recipient can unsubscribe, reports spam, and click on the same message',
          )}.`}
        </div>
      </div>
    </FilterCard>
  );
};

export default MessageTypeCardV2;

export const SendWasSkippedCard = () => {
  const {t} = useLabelTranslation();
  return (
    <FilterCard
      header={t('to_enable_filter', 'to enable filter')}
      className={css.skippedContainer}
    >
      <div className={css.message}>
        <p>
          {t('Set_Sent_or_Skipped_to_Sent', 'Set "Sent or Skipped?" to Sent')}
        </p>
      </div>
    </FilterCard>
  );
};

export const DeliverySetToBouncedCard = () => {
  const {t} = useLabelTranslation();
  return (
    <FilterCard
      header={t('to_enable_filter', 'to enable filter')}
      className={css.skippedContainer}
    >
      <div className={css.message}>
        <p>
          {t('Set_delivered_to_delivered', 'Set "delivered?" to delivered')}
        </p>
      </div>
    </FilterCard>
  );
};
